import { useSyncExternalStore } from "react";

import type { Origin } from "./types";

/** A question an attendee upload needs the planner to answer before it can finish. */
export interface AttendeeRequest {
  fileName: string;
  origins: Origin[];
  people: number;
  resolve: (accepted: boolean) => void;
}

let current: AttendeeRequest | null = null;
const listeners = new Set<() => void>();

const set = (next: AttendeeRequest | null) => {
  current = next;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Answers the open request as dismissed, so a second upload never leaves the first one hanging. */
const dismissOpen = () => {
  const open = current;
  if (!open) return;
  set(null);
  open.resolve(false);
};

/**
 * Shows what a document was read as and asks whether to use it.
 * @param fileName - The document, named in the dialog.
 * @param origins - The home cities read, with counts.
 * @param people - How many people they add up to.
 * @returns Whether the planner accepted.
 * @throws Never.
 */
export const askToConfirm = (fileName: string, origins: Origin[], people: number): Promise<boolean> =>
  new Promise((resolve) => {
    dismissOpen();
    set({
      fileName,
      origins,
      people,
      resolve: (accepted) => {
        set(null);
        resolve(accepted);
      },
    });
  });

/**
 * The open request, for the dialogs host. Any `useAttendeeFile` caller can raise one, including
 * those with no room for a modal of their own, so one host renders them all.
 * @returns The request, or null when none is open.
 * @throws Never.
 */
export const useAttendeeRequest = (): AttendeeRequest | null =>
  useSyncExternalStore(
    subscribe,
    () => current,
    () => null
  );
